// Browser capture only. Face detection and identity matching remain on the
// existing verification API; a captured frame is never proof of identity.
const PREVIEW_SETTLE_MS = 1000;

/** JPEG encoder quality for canvas.toBlob. Same bounds as getFaceAttendanceConfig. */
export function assertJpegCaptureQuality(quality) {
  const parsed = Number(quality);
  if (!Number.isFinite(parsed)) {
    throw new Error('FACE_ATTENDANCE_CAPTURE_QUALITY is invalid');
  }
  if (parsed <= 0 || parsed > 1) {
    throw new Error(
      'FACE_ATTENDANCE_CAPTURE_QUALITY must be greater than 0 and at most 1',
    );
  }
  return parsed;
}

function cameraError(error) {
  switch (error?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Camera permission was denied. Allow camera access for this site and try again.';
    case 'NotFoundError':
      return 'No camera was found. Connect a camera and try again.';
    case 'NotReadableError':
      return 'The camera is busy or unavailable. Close other camera apps and try again.';
    default:
      return error?.message || 'Unable to open the camera. Please try again.';
  }
}

export function startAttendanceCamera(video, options) {
  let captureQuality;
  try {
    captureQuality = assertJpegCaptureQuality(options.captureQuality);
  } catch (error) {
    options.onError(
      error instanceof Error
        ? error.message
        : 'FACE_ATTENDANCE_CAPTURE_QUALITY is invalid',
    );
    return function disposeRejectedCapture() {};
  }

  let disposed = false;
  let capturing = false;
  let stream;
  let captureTimer;
  let readyTimer;

  function dispose() {
    disposed = true;
    clearTimeout(captureTimer);
    clearTimeout(readyTimer);
    video.removeEventListener('playing', scheduleCapture);
    video.removeEventListener('loadeddata', scheduleCapture);
    video.removeEventListener('error', previewFailed);
    document.removeEventListener('visibilitychange', visibilityChanged);
    stream?.getTracks().forEach(track => {
      track.removeEventListener('ended', previewFailed);
      track.stop();
    });
    video.pause();
    video.srcObject = null;
  }

  function fail(message) {
    if (disposed) return;
    dispose();
    options.onError(message);
  }

  function previewFailed() {
    fail('The camera preview stopped. Please try again.');
  }

  function visibilityChanged() {
    if (document.visibilityState === 'hidden') {
      fail(
        'Camera capture was cancelled because the attendance page is no longer visible. Please try again.',
      );
    }
  }

  function capture() {
    if (disposed || capturing) return;
    if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
      captureTimer = undefined;
      return;
    }
    capturing = true;
    options.onStatus('Capturing…');
    try {
      const canvas = document.createElement('canvas');
      const scale = Math.min(
        1,
        options.imageMaxWidth / video.videoWidth,
        options.imageMaxHeight / video.videoHeight,
      );
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
      const context = canvas.getContext('2d');
      if (!context)
        throw new Error('This browser cannot capture a camera image.');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        blob => {
          if (disposed) return;
          if (!blob) {
            fail('Could not capture a clear photo. Please try again.');
            return;
          }
          const uri = URL.createObjectURL(blob);
          dispose();
          try {
            // Ownership passes to the existing verification service, which revokes
            // the URL after verification/action success or failure.
            options.onCaptured({
              uri,
              fileName: `attendance-${Date.now()}.jpg`,
              mimeType: 'image/jpeg',
              width: canvas.width,
              height: canvas.height,
            });
          } catch (error) {
            URL.revokeObjectURL(uri);
            options.onError(cameraError(error));
          }
        },
        'image/jpeg',
        captureQuality,
      );
    } catch (error) {
      fail(cameraError(error));
    }
  }

  function scheduleCapture() {
    if (
      disposed ||
      capturing ||
      captureTimer !== undefined ||
      !video.videoWidth ||
      !video.videoHeight
    )
      return;
    options.onStatus('Look at the camera — capturing automatically…');
    captureTimer = setTimeout(capture, PREVIEW_SETTLE_MS);
  }

  async function open() {
    if (globalThis.isSecureContext === false) {
      fail('Open this page using HTTPS or localhost to use the camera.');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      fail(
        'Camera access is unavailable in this browser. Use a browser with camera support over HTTPS or localhost.',
      );
      return;
    }
    try {
      const opened = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'user' },
          width: { ideal: options.imageMaxWidth },
          height: { ideal: options.imageMaxHeight },
        },
      });
      if (disposed) {
        opened.getTracks().forEach(track => track.stop());
        return;
      }
      stream = opened;
      stream
        .getTracks()
        .forEach(track => track.addEventListener('ended', previewFailed));
      video.srcObject = stream;
      readyTimer = setTimeout(
        () => fail('The camera did not provide an image. Please try again.'),
        options.timeoutMs,
      );
      await video.play();
      if (!disposed) scheduleCapture();
    } catch (error) {
      fail(cameraError(error));
    }
  }

  video.addEventListener('playing', scheduleCapture);
  video.addEventListener('loadeddata', scheduleCapture);
  video.addEventListener('error', previewFailed);
  document.addEventListener('visibilitychange', visibilityChanged);
  void open();
  return dispose;
}
